/* istanbul ignore file */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { SubscriptionHandle } from '@jack-henry/frontend-utils/observable';
import { DownloadService } from '@treasury/domain/download';
import { TmDetailedError } from '@treasury/domain/shared';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-context-menu';
import '@treasury/presentation/components/tm-icon';
import '@treasury/presentation/components/tm-loader';
import { html, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { download } from '../assets/icons';
import { TmContainer } from '../tm-container.component';
import { createActionItem, createHeader } from '../utils';
import { DownloadPageType } from './tm-download.types';

export const tagName = 'tm-download';
@customElement(tagName)
export class TmDownload<T = unknown> extends TmContainer {
    @InjectProperty()
    declare private downloadService: DownloadService<T>;

    @property({ attribute: false })
    private page?: DownloadPageType;

    @property({ attribute: false })
    private isRecurring = false;

    @property({ attribute: false })
    private filterModel: T = {} as T;

    @property({ attribute: false })
    private contextItems: Array<{ component: HTMLElement }> = [];

    @property({ type: Boolean })
    private isDownloading = false;

    @property({ type: Boolean })
    private disabled = false;

    private subs: SubscriptionHandle[] = [];

    private get pageId() {
        switch (this.page) {
            case DownloadPageType.Accounts:
                return 'AccountList';
            case DownloadPageType.ACH:
                return this.isRecurring ? 'RecurringACHPaymentList' : 'ACHPaymentList';
            case DownloadPageType.AchTemplates:
                return 'ACHTemplateList';
            case DownloadPageType.AchExceptions:
                return 'AchExceptionsList';
            case DownloadPageType.CheckExceptions:
                return 'CheckExceptions';
            case DownloadPageType.Loans:
                return 'LoanPaymentList';
            case DownloadPageType.ResearchTransactions:
                return 'ResearchTransactionsList';
            case DownloadPageType.StopPayments:
                return 'StopPayRequestList';
            case DownloadPageType.Transfers:
                return this.isRecurring ? 'RecurringTransferList' : 'TransferList';
            case DownloadPageType.TransferTemplates:
                return 'TransferTemplateList';
            case DownloadPageType.WireCreditors:
                return 'CreditorList';
            case DownloadPageType.WireTemplates:
                return 'WireTemplateList';
            case DownloadPageType.Wires:
                return this.isRecurring ? 'RecurringWireList' : 'WireActivity';
            default:
                return '';
        }
    }

    private get formats() {
        switch (this.page) {
            case DownloadPageType.ACH:
            case DownloadPageType.Wires:
                return this.isRecurring
                    ? ['PDF']
                    : ['Summary - PDF', 'Detail - PDF', 'Summary - CSV', 'Detail - CSV'];
            case DownloadPageType.Accounts:
            case DownloadPageType.AchExceptions:
            case DownloadPageType.CheckExceptions:
            case DownloadPageType.ResearchTransactions:
                return ['PDF', 'CSV'];
            default:
                return ['PDF'];
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();
        const sub = this.downloadService.configSource.subscribe(config => {
            this.filterModel = config.filterModel;
            this.isRecurring = config.isRecurring;
            this.disabled = config.disabled;
        });
        this.subs.push(sub);
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.subs.forEach(sub => {
            sub.unsubscribe();
        });
    }

    protected updated(changedProps: PropertyValues): void {
        if (changedProps.has('filterModel')) {
            this.contextItems = [{ component: createHeader('Download', undefined, 'close') }];
            this.formats.forEach(item => {
                this.contextItems.push({ component: createActionItem(download, item) });
            });
        }
    }

    protected render() {
        return this.isDownloading
            ? html`<div class="flex justify-center w-9">
                  <tm-loader reversed></tm-loader>
              </div>`
            : html`<tm-context-menu
                  class=${classMap({
                      hidden: this.disabled && !this.filterModel,
                  })}
                  .items=${this.contextItems}
                  .openOn=${'click'}
                  .closeOn=${'close'}
                  @item-selected=${({ detail }: CustomEvent) => {
                      const outputType = detail.value.text
                          ? detail.value.text
                          : detail.value.component.textContent;
                      this.download(outputType);
                  }}
              >
                  <tm-button
                      importance="tertiary"
                      .disabled=${this.disabled}
                      class=${classMap({ 'opacity-50': this.disabled })}
                      ><tm-icon .icon=${download} color="white"></tm-icon
                  ></tm-button>
              </tm-context-menu>`;
    }

    private async download(outputType: string) {
        this.isDownloading = true;
        try {
            await this.downloadService.download(
                outputType,
                this.filterModel as object,
                this.pageId
            );
        } catch (error) {
            if (error instanceof TmDetailedError) {
                this.notificationService.renderError(error);
            } else if (error instanceof Error) {
                this.notificationService.renderError(error);
            } else {
                this.notificationService.renderError('Error processing your download');
            }
        } finally {
            this.isDownloading = false;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmDownload;
    }
}
